// src/modules/units/tyres/shared/tyre-import-schema.js
//
// What a spreadsheet column can be imported INTO, and how a header is recognised.
// Pure — no DOM, no I/O.
//
// IMPORT_FIELDS mirrors the POST /api/tyre request body (Swagger), minus
// `customerId`, which is never a column: it comes from the target yard's owning
// customer (see tyre-import-validate.js). Order is the order the mapping dropdown
// lists them in: identity → specification → condition → purchase → placement.
//
// Types drive coercion and validation in tyre-import-validate.js:
//   text     trimmed string
//   vocab    string matched case-insensitively onto a canonical list (off-list
//            values are kept as entered with a warning — the New Tyre form allows
//            custom values too)
//   number   decimal, >= 0, rounded to `dp` places
//   size     text; a size that is one of TYRE_SIZES written differently
//            ("315/80R22.5" for "315/80/R22.5") takes the list's spelling so the
//            record and catalogue selects recognise it; any other size is kept
//            as entered, silently — sizes are an open list
//   integer  whole number, >= 0 (a fraction is rounded with a warning)
//   date     'YYYY-MM-DD'
//   supplier resolved by supplier NAME or id to supplierId
//   yard     resolved by yard NAME or id to yardId; a filled cell overrides the
//            yard chosen for the import, a blank one falls back to it
//
// Aliases are compared after normaliseHeader(), so "Purchase cost (ZAR)",
// "purchase_cost" and "PURCHASE COST" all match the alias "purchase cost".

import {
  TYRE_SIZES, TYRE_TYPES, TYRE_APPLICATIONS, TYRE_GRADES, WEAR_PATTERNS
} from './tyre-vocab.js';

export const IMPORT_FIELDS = Object.freeze([
  {
    key: 'externalTyreId', label: 'Tyre ID', type: 'text', required: true, group: 'Identity',
    aliases: ['tyre id', 'tyre no', 'tyre number', 'tyre ref', 'tyre reference', 'tyre code',
      'external tyre id', 'externaltyreid', 'tyreid', 'id', 'fleet tyre no', 'fleet number', 'asset number', 'asset no']
  },
  {
    key: 'tyreSerialNumber', label: 'Serial number', type: 'text', group: 'Identity',
    aliases: ['serial number', 'serial no', 'serial', 'tyre serial number', 'tyre serial', 'casing serial', 'dot serial', 'sn']
  },
  {
    key: 'tyreRFID', label: 'RFID', type: 'text', group: 'Identity',
    aliases: ['rfid', 'rfid tag', 'tyre rfid', 'rfid number', 'tag']
  },

  {
    key: 'tyreMake', label: 'Make', type: 'text', group: 'Specification',
    aliases: ['make', 'tyre make', 'manufacturer', 'tyre manufacturer']
  },
  {
    key: 'tyreBrand', label: 'Brand', type: 'text', group: 'Specification',
    aliases: ['brand', 'tyre brand', 'brand name', 'manufacturer brand', 'manufacturer and brand']
  },
  {
    key: 'tyreModel', label: 'Model', type: 'text', group: 'Specification',
    aliases: ['model', 'tyre model', 'model name']
  },
  {
    key: 'tyreTreadPattern', label: 'Tread pattern', type: 'text', group: 'Specification',
    aliases: ['tread pattern', 'tyre tread pattern', 'pattern', 'tread pattern code', 'tread design']
  },
  {
    key: 'tyreSize', label: 'Size', type: 'size', vocab: TYRE_SIZES, group: 'Specification',
    aliases: ['size', 'tyre size', 'dimension', 'dimensions']
  },
  {
    key: 'tyreType', label: 'Type', type: 'vocab', vocab: TYRE_TYPES, group: 'Specification',
    aliases: ['type', 'tyre type']
  },
  {
    key: 'tyreApplication', label: 'Application', type: 'vocab', vocab: TYRE_APPLICATIONS, group: 'Specification',
    aliases: ['application', 'tyre application', 'axle type', 'wheel type', 'position type', 'usage']
  },
  {
    key: 'tyreGrade', label: 'Grade', type: 'vocab', vocab: TYRE_GRADES, group: 'Specification',
    aliases: ['grade', 'tyre grade', 'condition grade']
  },
  {
    key: 'manufacturerExpectedMileage', label: 'Manufacturer expected mileage (km)', type: 'integer', group: 'Specification',
    aliases: ['manufacturer expected mileage', 'expected mileage', 'expected km', 'rated mileage', 'expected life km']
  },
  {
    key: 'maxRetreads', label: 'Maximum retreads', type: 'integer', max: 10, group: 'Specification',
    aliases: ['maximum retreads', 'max retreads', 'retreads allowed', 'retread limit', 'max retread']
  },

  {
    key: 'initialTyreTreadDepth', label: 'Initial tread (mm)', type: 'number', dp: 2, max: 40, group: 'Condition',
    aliases: ['initial tread depth', 'initial tread', 'original tread depth', 'original tread', 'new tread depth',
      'otd', 'initial tyre tread depth', 'nominal tread depth']
  },
  {
    key: 'tyreTreadDepth', label: 'Current tread (mm)', type: 'number', dp: 2, max: 40, group: 'Condition',
    // A bare "Tread" column is read as a depth (it is how fleet lists usually
    // label the measurement); the sample values on the mapping screen show it.
    aliases: ['current tread depth', 'current tread', 'tread depth', 'remaining tread', 'remaining tread depth',
      'rtd', 'tyre tread depth', 'measured tread', 'tread']
  },
  {
    key: 'minimumTreadForRetread', label: 'Minimum tread for retread (mm)', type: 'number', dp: 2, max: 40, group: 'Condition',
    aliases: ['minimum tread for retread', 'min tread for retread', 'minimum tread', 'min tread', 'retread tread limit']
  },
  {
    key: 'tyreWearPattern', label: 'Wear pattern', type: 'vocab', vocab: WEAR_PATTERNS, group: 'Condition',
    aliases: ['wear pattern', 'tyre wear pattern', 'wear']
  },
  {
    key: 'tyreKm', label: 'Opening distance (km)', type: 'integer', group: 'Condition',
    aliases: ['kilometres', 'kilometers', 'km', 'kms', 'tyre km', 'opening distance', 'distance', 'odometer',
      'km travelled', 'km run', 'mileage', 'current km']
  },

  {
    key: 'purchaseDate', label: 'Purchase / invoice date', type: 'date', group: 'Purchase',
    aliases: ['purchase date', 'invoice date', 'date purchased', 'date of purchase', 'bought', 'date']
  },
  {
    key: 'purchaseCost', label: 'Purchase cost (excl. VAT)', type: 'number', dp: 2, group: 'Purchase',
    aliases: ['purchase cost', 'cost', 'price', 'purchase price', 'unit cost', 'cost excl vat', 'purchase cost excl vat', 'amount']
  },
  {
    key: 'invoiceNo', label: 'Invoice number', type: 'text', group: 'Purchase',
    aliases: ['invoice number', 'invoice no', 'invoice', 'inv no', 'invoice ref', 'invoice reference']
  },
  {
    key: 'supplierId', label: 'Supplier', type: 'supplier', group: 'Purchase',
    aliases: ['supplier', 'supplier name', 'supplier id', 'vendor', 'dealer']
  },

  {
    key: 'yardId', label: 'Yard (per row)', type: 'yard', group: 'Placement',
    aliases: ['yard', 'yard id', 'yard name', 'depot', 'site', 'branch']
  },
  {
    key: 'notes', label: 'Notes', type: 'text', group: 'Placement',
    aliases: ['notes', 'note', 'comments', 'comment', 'remarks']
  }
]);

const BY_KEY = new Map(IMPORT_FIELDS.map((f) => [f.key, f]));

export function getImportField(key) {
  return BY_KEY.get(key) || null;
}

export const REQUIRED_FIELD_KEYS = IMPORT_FIELDS.filter((f) => f.required).map((f) => f.key);

/**
 * Header text → comparison key. Drops bracketed units/notes ("(mm)", "[ZAR]"),
 * turns punctuation and underscores into spaces, splits camelCase, lower-cases.
 */
export function normaliseHeader(h) {
  return String(h ?? '')
    .replace(/\(.*?\)|\[.*?\]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const ALIAS_INDEX = (() => {
  const m = new Map();
  for (const f of IMPORT_FIELDS) {
    for (const a of [f.key, f.label, ...f.aliases]) {
      const k = normaliseHeader(a);
      if (k && !m.has(k)) m.set(k, f.key);
    }
  }
  return m;
})();

/** The field a header most likely means, or '' when nothing matches. */
export function guessField(header) {
  return ALIAS_INDEX.get(normaliseHeader(header)) || '';
}

/**
 * Suggest a mapping for a header row: one field key (or '') per column. Each field
 * is used at most once; when two columns want the same field, the leftmost wins
 * and the other is left unmapped for the user to decide.
 */
export function autoMap(headers = []) {
  const used = new Set();
  return headers.map((h) => {
    const key = guessField(h);
    if (!key || used.has(key)) return '';
    used.add(key);
    return key;
  });
}

/**
 * Validate a mapping. Returns user-facing problems that block moving on:
 * a required field unmapped, or a field mapped from two columns.
 */
export function mappingProblems(mapping = [], headers = []) {
  const problems = [];
  const seen = new Map();
  mapping.forEach((key, col) => {
    if (!key) return;
    if (seen.has(key)) {
      const f = getImportField(key);
      problems.push(`“${f?.label || key}” is mapped from two columns (${headerName(headers, seen.get(key))} and ${headerName(headers, col)}).`);
    } else seen.set(key, col);
  });
  for (const key of REQUIRED_FIELD_KEYS) {
    if (!seen.has(key)) problems.push(`Map a column to “${getImportField(key).label}” — it is required.`);
  }
  return problems;
}

function headerName(headers, col) {
  const h = headers[col];
  return h ? `“${h}”` : `column ${columnLetter(col)}`;
}

/** 0 → "A", 27 → "AB" */
export function columnLetter(i) {
  let n = i + 1;
  let s = '';
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

/**
 * Guess which row holds the headers: among the first 20 rows, the one whose cells
 * match the most known aliases (ties → the earliest). Falls back to the first
 * row with at least two text cells, then to row 0.
 */
export function detectHeaderRow(rows = []) {
  let best = -1;
  let bestScore = 0;
  const limit = Math.min(rows.length, 20);
  for (let i = 0; i < limit; i++) {
    const score = (rows[i] || []).filter((c) => typeof c === 'string' && guessField(c)).length;
    if (score > bestScore) { best = i; bestScore = score; }
  }
  if (best >= 0) return best;
  for (let i = 0; i < limit; i++) {
    if ((rows[i] || []).filter((c) => typeof c === 'string' && c.trim()).length >= 2) return i;
  }
  return 0;
}

/**
 * Header cells for the chosen header row, padded to the widest data row so a
 * column with data but no heading can still be mapped ("Column F").
 */
export function headersFor(rows = [], headerRow = 0) {
  const width = rows.reduce((w, r) => Math.max(w, (r || []).length), 0);
  const raw = rows[headerRow] || [];
  return Array.from({ length: width }, (_, i) => {
    const h = raw[i];
    return h === null || h === undefined || String(h).trim() === '' ? '' : String(h).trim();
  });
}

/**
 * A stable key for "this layout of spreadsheet", used to remember a mapping. Two
 * files with the same headers in the same order share it; any change starts fresh.
 */
export function headerSignature(headers = []) {
  return headers.map(normaliseHeader).join('|');
}
